import { Component, EventEmitter, HostListener, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { LocalStorageService, SharedService } from '../../services';
import { LocalStorage, Paths } from '../../enums';
import { CompanyDetailSelectAllOutput, CompanyDetailServiceProxy } from '../../service-proxies';
import { filter } from 'rxjs/operators';
import { Subscription } from 'rxjs';

@Component({
  selector: 'gps-tracker-top-nav',
  standalone: false,
  templateUrl: './top-nav.component.html',
  styleUrl: './top-nav.component.scss'
})
export class TopNavComponent implements OnInit, OnDestroy {
  @Input() isMenuOpen = true;
  @Output() menuToggle: EventEmitter<any> = new EventEmitter();

  showProfilePopup = false;
  userData: any = null;
  userCompanyName: string = 'Gps Tracking System';
  dropdownReady: boolean = false;
  isOnReportModule: boolean = false;

  companyNames: any[] = [];
  companyData: any[] = [];
  selectedCompany: string = '';
  isAdmin: boolean = false;
  shouldHideCompanyDropdown: boolean = false;
  companyDataLoaded: boolean = false;
  defaultCompanyId: number = 1;

  private routerSubscription?: Subscription;

  constructor(
    private localStorageService: LocalStorageService,
    private router: Router,
    private companyDetailServiceProxy: CompanyDetailServiceProxy,
    private sharedService: SharedService
  ) {
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe(() => {
      this.checkIfAddEditPage();
    });
  }

  ngOnInit(): void {
    const loginData = this.localStorageService.getLoginData();
    this.isAdmin = loginData?.user_type_id === 1;

    this.checkIfReportModule();
    this.initializeCompanyDropdown();
    this.loadUserData();
    this.checkIfAddEditPage();

    this.routerSubscription = this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd)
    ).subscribe(() => {
      this.checkIfReportModule();
      if (this.isAdmin && this.companyDataLoaded) {
        this.buildCompanyDropdownOptions();
      }
    });
  }

  ngOnDestroy(): void {
    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
    }
  }

  buildCompanyDropdownOptions(): void {
    if (!this.companyData || this.companyData.length === 0) {
      return;
    }

    if (this.isOnReportModule) {
      this.companyNames = [
        {
          'company_details_id': 0,
          'company_name': 'Select All'
        },
        ...this.companyData
      ];
    } else {
      this.companyNames = [...this.companyData];

      if (this.selectedCompany === 'Select All') {
        this.selectedCompany = this.companyNames[0]?.company_name || '';
        this.onCompanyChange(this.selectedCompany);
      }
    }
  }

  checkIfReportModule(): void {
    const currentUrl = this.router.url.toLowerCase();
    const reportRoutes = [
      '/application/reports/route-reports',
      '/application/reports/event-reports',
      '/application/reports/trips-reports',
      '/application/reports/stops-reports',
      '/application/reports/summary-reports',
      '/application/reports/geofence-reports',
      '/application/reports/poi-reports',
      'route-reports',
      'event-reports',
      'trips-reports',
      'stops-reports',
      'summary-reports',
      'geofence-reports',
      'poi-reports'
    ];

    this.isOnReportModule = reportRoutes.some(route => currentUrl.includes(route));
  }

  checkIfAddEditPage(): void {
    const currentUrl = this.router.url;
    const urlSegments = currentUrl.split('/').filter(segment => segment.length > 0);

    const editPatterns = ['add-edit-', 'add-', 'edit-', 'update-'];
    const isAddEditPage = editPatterns.some(pattern => currentUrl.includes(pattern));

    let idSegmentCount = 0;
    for (const segment of urlSegments) {
      if (/^\d+$/.test(segment) || segment.includes('-')) {
        idSegmentCount++;
      }
    }
    const isNestedDetailPage = urlSegments.length > 4 && idSegmentCount >= 1;

    const patterns = [
      'company-map', 'vehicle-document', 'geofence-vehicle', 'poi-vehicles',
      'temperature-unit', 'alert-specification', 'application-sub-menu',
      'state', 'city', 'device-model', 'device-model-port-specification',
      'tool-tip-option', 'tyre-model'
    ];
    const isSpecificNestedPage = patterns.some(pattern => currentUrl.includes(pattern));

    const isCompanyRoute = currentUrl.endsWith('/company') || currentUrl.endsWith('/company/');

    const isAddEditCompanyRulePage = currentUrl.includes(Paths.companyRule + '/' + Paths.addCompanyRule) ||
      currentUrl.includes(Paths.companyRule + '/' + Paths.editCompanyRule);

    this.shouldHideCompanyDropdown = (isAddEditPage && !isAddEditCompanyRulePage) ||
      isNestedDetailPage ||
      isSpecificNestedPage ||
      isCompanyRoute;
  }

  loadUserData() {
    const loginData = this.localStorageService.getLoginData();
    if (loginData) {
      this.userData = loginData;
      const storedName = this.localStorageService.getData(LocalStorage.companyName);
      if (storedName && !this.isAdmin) {
        this.userCompanyName = storedName;
      }
    }
  }

  initializeCompanyDropdown(): void {
    const loginData = this.localStorageService.getLoginData();
    this.isAdmin = Number(loginData?.user_type_id) === 1;
    if (this.isAdmin) {
      if (!this.companyDataLoaded) {
        this.companyDataLoaded = true;
        this.companyDetailServiceProxy.selectAllCompany()
          .subscribe((resp: CompanyDetailSelectAllOutput) => {
            if (resp.status === 1) {
              this.companyData = resp.data;
              this.buildCompanyDropdownOptions();

              const storedCompanyId = this.localStorageService.getData(LocalStorage.companyDetailsId);

              if (storedCompanyId) {
                if (storedCompanyId === '0' && this.isOnReportModule) {
                  this.selectedCompany = 'Select All';
                  this.setUserCompanyName(0);
                } else if (storedCompanyId === '0' && !this.isOnReportModule) {
                  this.selectedCompany = this.companyNames[0]?.company_name || '';
                  this.updateCompanyId();
                  this.setUserCompanyName(this.companyNames[0]?.company_details_id || this.defaultCompanyId);
                } else {
                  const storedCompany = this.companyData.find(c => c.company_details_id === Number(storedCompanyId));
                  if (storedCompany) {
                    this.selectedCompany = storedCompany.company_name;
                    this.setUserCompanyName(storedCompany.company_details_id);
                  } else {
                    const defaultEntry = this.companyData.find(c => c.company_details_id === this.defaultCompanyId);
                    this.selectedCompany = defaultEntry ? defaultEntry.company_name : (this.companyNames[0]?.company_name || '');
                    this.updateCompanyId();
                    this.setUserCompanyName(this.defaultCompanyId);
                  }
                }
              } else {
                const defaultEntry = this.companyData.find(c => c.company_details_id === this.defaultCompanyId);
                this.selectedCompany = defaultEntry ? defaultEntry.company_name : (this.companyNames[0]?.company_name || '');
                this.updateCompanyId();
                this.setUserCompanyName(this.defaultCompanyId);
              }

              this.dropdownReady = true;
              this.sharedService.setSelectedCompany(this.selectedCompany);
            }
          });
      }
    }
    else {
      if (loginData && loginData.company_details_id) {
        const companyId = Number(loginData.company_details_id);
        this.companyDetailServiceProxy.selectCompanyById(companyId).subscribe({
          next: (response) => {
            if (response.status === 1 && response.data) {
              this.userCompanyName = response.data.company_name;
              this.sharedService.setSelectedCompany(this.userCompanyName);
              this.localStorageService.setData(LocalStorage.companyName, this.userCompanyName);
              this.localStorageService.setData(LocalStorage.companyDetailsId, companyId);
            }
          },
          error: (err) => console.error('Failed to fetch user company details', err)
        });
      } else {
        this.userCompanyName = 'N/A';
      }
    }
    this.sharedService.selectedCompany$
      .subscribe(name => {
        this.selectedCompany = name;
        if (this.isAdmin) {
        }
      });
  }

  setUserCompanyName(companyDetailsId: number): void {
    if (companyDetailsId === 0) {
      this.localStorageService.setData(LocalStorage.companyName, 'Select All');
    } else {
      const companyEntry = this.companyData.find((x: any) => x.company_details_id === companyDetailsId);
      if (companyEntry) {
        this.localStorageService.setData(LocalStorage.companyName, companyEntry.company_name);
      }
    }
  }

  private updateCompanyId(): void {
    if (this.companyNames && this.selectedCompany && this.selectedCompany !== 'Select All') {
      const selectedCompanyData = this.companyNames.find((x: any) => x.company_name === this.selectedCompany);
      if (selectedCompanyData) {
        this.localStorageService.setData(LocalStorage.companyDetailsId, selectedCompanyData.company_details_id);
        this.localStorageService.setData(LocalStorage.companyName, selectedCompanyData.company_name);
      }
    }
  }

  onCompanyChange(name: string): void {
    this.selectedCompany = name;

    if (name === 'Select All' && this.isOnReportModule) {
      this.localStorageService.setData(LocalStorage.companyDetailsId, '0');
      this.localStorageService.setData(LocalStorage.companyName, 'Select All');
      this.sharedService.setSelectedCompany('Select All');
    } else if (name === 'Select All' && !this.isOnReportModule) {
      this.selectedCompany = this.companyNames[0]?.company_name || '';
      this.updateCompanyId();
      this.sharedService.setSelectedCompany(this.selectedCompany);
    } else {
      this.updateCompanyId();
      this.sharedService.setSelectedCompany(name);
    }
  }

  onHideShow(): void {
    this.menuToggle.emit(!this.isMenuOpen);
  }

  logout() {
    this.localStorageService.deleteData(LocalStorage.accessToken);
    this.localStorageService.deleteData(LocalStorage.refreshToken);
    this.localStorageService.deleteData(LocalStorage.userMenu);
    this.localStorageService.deleteData(LocalStorage.loginData);
    this.localStorageService.deleteData(LocalStorage.applicationMenuId);
    this.localStorageService.deleteData(LocalStorage.applicationSubMenuId);
    this.localStorageService.deleteData(LocalStorage.companyDetailsId);
    this.router.navigate(["/" + Paths.authentication]);
  }

  toggleProfilePopup() {
    this.showProfilePopup = !this.showProfilePopup;
  }

  @HostListener('document:click', ['$event'])
  onClickOutside(event: MouseEvent) {
    const profileToggle = document.querySelector('.profile-toggle');
    const profilePopup = document.querySelector('.profile-popup');

    if (profileToggle && profilePopup) {
      if (!profileToggle.contains(event.target as Node) &&
        !profilePopup.contains(event.target as Node) &&
        this.showProfilePopup) {
        this.showProfilePopup = false;
      }
    }
  }
}
